import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import { apiService, LeadSearchSuggestion, LeadSearchSuggestions as Suggestions } from '../../services/api';
import { useAppStore } from '../../store/appStore';
import { useLocale } from '../../contexts/LocaleContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { Button } from '../design-system';
import { Chip, regionLabel } from './leadFinderUi';
import { colors, spacing, typography } from '../../theme';
import type { TranslationKey } from '../../i18n/en';

export const LeadSearchSuggestions: React.FC<{ onReview: (suggestion: LeadSearchSuggestion) => void }> = ({ onReview }) => {
  const businessId = useAppStore(s => s.currentBusiness?._id);
  const role = useAppStore(s => s.businessRole);
  const { hasPermissionAtLeast } = usePermissions();
  const canSuggest = role === 'owner' || role === 'admin' || hasPermissionAtLeast('general.leadFinder', 'edit');
  const { t, locale } = useLocale();
  const rtl = useRTLStyles();
  const styles = useThemedStyles(getStyles);
  const [savedData, setData] = useState<(Suggestions & { businessId: string }) | null>(null);
  const data = savedData?.businessId === businessId ? savedData : null;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const currentBusiness = useRef(businessId);
  currentBusiness.current = businessId;
  const requestVersion = useRef(0);
  const selectedPersona = useRef<string | undefined>(undefined);
  const load = useCallback(async (agentId?: string) => {
    const version = ++requestVersion.current;
    const current = () => currentBusiness.current === businessId && requestVersion.current === version;
    setBusy(true); setError('');
    if (agentId) selectedPersona.current = agentId;
    try {
      const result = await apiService.suggestLeadSearches({ agentId: agentId || selectedPersona.current, language: /^ar/i.test(locale) ? 'ar' : 'en' });
      if (!current()) return;
      if (result.statusCode !== 1000 || !result.data) throw new Error(result.errors?.[0]?.message || t('leadFinder.suggestions.failed'));
      selectedPersona.current = result.data.persona?.id;
      setData({ ...result.data, businessId: businessId || '' });
    } catch (err: any) { if (current()) { setData(null); setError(err?.message || t('leadFinder.suggestions.failed')); } }
    finally { if (current()) setBusy(false); }
  }, [businessId, locale, t]);

  useEffect(() => {
    selectedPersona.current = undefined;
    setData(null); setError('');
    if (businessId && canSuggest) void load();
    return () => { requestVersion.current++; };
  }, [businessId, canSuggest, load]);

  if (!canSuggest) return null;
  return <View style={styles.section}>
    <Text style={[styles.title, { textAlign: rtl.textStart }]}>{t('leadFinder.suggestions.title')}</Text>
    <Text style={[styles.meta, { textAlign: rtl.textStart }]}>{t('leadFinder.suggestions.hint')}</Text>
    {data ? <>
      <Text style={[styles.meta, { textAlign: rtl.textStart }]}>
        {[data.businessName, data.persona?.name].filter(Boolean).join(' · ')}
      </Text>
      <Text style={[styles.meta, { textAlign: rtl.textStart }]}>
        {data.basedOn.map(source => t(`leadFinder.suggestions.source.${source}` as TranslationKey)).join(' · ')}
      </Text>
      {data.personas.length > 1 ? <View style={[styles.chips, { flexDirection: rtl.rowDirection }]}>
        {data.personas.map(persona => <Chip key={persona.id} label={persona.name} selected={data.persona?.id === persona.id}
          onPress={() => { if (!busy && persona.id !== data.persona?.id) { setData(null); void load(persona.id); } }} />)}
      </View> : null}
    </> : null}
    {busy ? <View style={[styles.loading, { flexDirection: rtl.rowDirection }]}>
      <ActivityIndicator color={colors.primary as string} /><Text style={styles.meta}>{t('leadFinder.suggestions.loading')}</Text>
    </View> : null}
    {error ? <Text accessibilityRole="alert" style={[styles.error, { textAlign: rtl.textStart }]}>{error}</Text> : null}
    {!busy && data?.suggestions.map((suggestion, index) => <View key={index} style={styles.proposal}>
      <Text style={[styles.title, { textAlign: rtl.textStart }]}>{suggestion.title}</Text>
      <Text style={[styles.meta, { textAlign: rtl.textStart }]}>{suggestion.reason}</Text>
      <Text style={[styles.prompt, { textAlign: rtl.textStart }]}>{suggestion.prompt}</Text>
      <Text style={[styles.meta, { textAlign: rtl.textStart }]}>
        {[regionLabel(suggestion.settings.region), ...(suggestion.settings.professions?.length ? suggestion.settings.professions : suggestion.settings.keywords || [])].filter(Boolean).join(' · ')}
      </Text>
      {suggestion.settings.targetType === 'individual' ? <Text style={[styles.meta, { textAlign: rtl.textStart }]}>{t('leadFinder.criteria.individualHint')}</Text> : null}
      <Button title={t('leadFinder.suggestions.review')} onPress={() => onReview(suggestion)} size="sm" variant="primary" />
    </View>)}
    <Button title={data ? t('leadFinder.suggestions.refresh') : t('common.retry')} onPress={() => void load(data?.persona?.id)} disabled={busy} size="sm" variant="secondary" />
  </View>;
};

const getStyles = () => StyleSheet.create({
  section: { gap: spacing.sm, paddingVertical: spacing.sm },
  title: { color: colors.text.primary, fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold },
  meta: { color: colors.text.secondary, fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.regular },
  prompt: { color: colors.text.primary, fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular },
  chips: { flexWrap: 'wrap', gap: spacing.xs },
  proposal: { borderWidth: 1, borderColor: colors.border, borderRadius: 12, padding: spacing.sm, gap: spacing.sm },
  loading: { alignItems: 'center', gap: spacing.sm },
  error: { color: colors.status.error as string, fontSize: typography.fontSize.sm },
});
